import React, { useEffect, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ReactTable from "react-table";
import Select from "react-select";

import History from "@material-ui/icons/History";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";

import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";
import { ValidSelect } from "beaktShared/helpers/validselect";
import { UseGetApiSimple } from "beaktShared/services/crudeApiService";
import { SessionContext, getLanguageSelected } from "beaktShared/services/session";
import { getAuditoriaContrato, getAcaoInfo, rotuloCampo, opcoesDistintas, filtrarAuditoria } from "beaktMatriz/views/Royalties/AuditoriaContratoService.js";

import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/crudeStyle.js";

const useStyles = makeStyles(styles);

// BKT-1514: formato de data conforme o idioma da sessão
const formatDateTime = (value) => (value
  ? moment(value).format(getLanguageSelected() === "en" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss")
  : "");

const badgeStyle = (info) => ({
  display: "inline-block",
  color: info.color,
  background: info.background,
  padding: "3px 8px",
  borderRadius: "4px",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "1.4",
  wordBreak: "break-word",
});

// BKT-1510: histórico de contrato/reajuste por unidade (índice aplicado, piso, bloqueios e alterações de contrato)
export default function AuditoriaContrato() {
  const { t } = useTranslation();
  const classes = useStyles();
  const session = useContext(SessionContext);

  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [loadingEstabelecimentos, setLoadingEstabelecimentos] = useState(false);
  const [estabelecimento, setEstabelecimento] = useState(null);
  const [erroEstabelecimento, setErroEstabelecimento] = useState(false);

  const [registros, setRegistros] = useState([]);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtroAcao, setFiltroAcao] = useState(null);
  const [filtroContrato, setFiltroContrato] = useState(null);

  const [notification, setNotification] = useState({ show: false, color: "info", message: "" });
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  useEffect(() => {
    setLoadingEstabelecimentos(true);
    // BKT-1514: inclui unidades inativas/migradas — o histórico do contrato delas também é consultável
    UseGetApiSimple("estabelecimentos/byfranquia/" + session.franquiaId, 0).then((result) => {
      if (!montadoRef.current)
        return;
      setLoadingEstabelecimentos(false);
      if (result && result.status === 200 && Array.isArray(result.data))
        setEstabelecimentos(result.data);
      else // BKT-1514: falha ao carregar unidades não pode parecer lista vazia
        setNotification({ show: true, color: "danger", message: (result && result.message) || t("contract_audit.units_load_error") });
    });
  }, [session.franquiaId, t]);

  // Mensagens somem após 6s; o timer é cancelado se a tela sair antes
  useEffect(() => {
    if (!notification.show)
      return;
    const timer = setTimeout(() => setNotification({ show: false }), 6000);
    return () => clearTimeout(timer);
  }, [notification]);

  const handleClickPesquisa = () => {
    if (!estabelecimento) {
      setErroEstabelecimento(true);
      return;
    }
    setErroEstabelecimento(false);
    setLoading(true);
    setBuscaRealizada(true);
    setFiltroAcao(null);
    setFiltroContrato(null);

    getAuditoriaContrato(estabelecimento.estabelecimentoId).then((result) => {
      if (!montadoRef.current)
        return;
      setLoading(false);
      setRegistros(result.data);
      if (!result.ok)
        setNotification({ show: true, color: "danger", message: result.message || t("contract_audit.load_error") });
    });
  };

  const opcoesAcao = opcoesDistintas(registros, "tipoAcao", "tipoAcaoDescricao");
  const opcoesContrato = opcoesDistintas(registros, "tipoContrato", "tipoContrato");
  const dadosTabela = filtrarAuditoria(registros, {
    tipoAcao: filtroAcao ? filtroAcao.value : null,
    tipoContrato: filtroContrato ? filtroContrato.value : null,
  });

  // BKT-1613: larguras mínimas menores e texto quebrando na célula — a grade cabe em 1280px com o menu aberto;
  // abaixo disso a própria tabela rola na horizontal
  const quebraTexto = { whiteSpace: "normal", wordBreak: "break-word" };
  const columns = [
    {
      Header: t("contract_audit.date_time"),
      accessor: "dataHora",
      minWidth: 100,
      style: quebraTexto,
      Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
    },
    {
      Header: t("contract_audit.contract_type"),
      accessor: "tipoContrato",
      minWidth: 95,
      style: quebraTexto,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: t("contract_audit.action"),
      accessor: "tipoAcao",
      minWidth: 150,
      style: quebraTexto,
      Cell: ({ original }) => {
        const info = getAcaoInfo(original);
        return <span style={badgeStyle(info)}>{info.label}</span>;
      },
    },
    {
      Header: t("contract_audit.origin"),
      accessor: "origem",
      minWidth: 70,
      style: quebraTexto,
    },
    {
      Header: t("contract_audit.field"),
      accessor: "campo",
      minWidth: 95,
      style: quebraTexto,
      Cell: ({ value }) => <span>{rotuloCampo(value, t)}</span>,
    },
    {
      Header: t("contract_audit.previous_value"),
      accessor: "valorAnterior",
      minWidth: 90,
      style: quebraTexto,
    },
    {
      Header: t("contract_audit.new_value"),
      accessor: "valorNovo",
      minWidth: 90,
      style: quebraTexto,
    },
    {
      Header: t("label.user"),
      accessor: "usuario",
      minWidth: 100,
      style: quebraTexto,
    },
    {
      Header: t("contract_audit.observation"),
      accessor: "observacao",
      minWidth: 150,
      style: quebraTexto,
    },
  ];

  return (
    <div>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <History />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>{t("sidebar.contract_audit")}</h4>
              <p>{t("contract_audit.subtitle")}</p>
            </CardHeader>
            <CardBody>
              <GridContainer style={{ marginBottom: "20px" }}>
                <GridItem xs={12} sm={12} md={6}>
                  <FormControl fullWidth className={classes.formControl} style={{ marginBottom: "20px" }}>
                    <label className={erroEstabelecimento ? "select__label select__label_error" : "select__label"}>
                      {t("label.establishment")} *
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      classNamePrefix="select"
                      isSearchable
                      styles={ValidSelect(erroEstabelecimento)}
                      isLoading={loadingEstabelecimentos}
                      placeholder={t("label.select") + "..."}
                      options={estabelecimentos}
                      getOptionLabel={({ descricao, ativo }) => ativo === false ? `${descricao} (${t("label.inactive")})` : descricao}
                      getOptionValue={({ estabelecimentoId }) => estabelecimentoId}
                      value={estabelecimento}
                      onChange={(value) => {
                        setEstabelecimento(value);
                        setErroEstabelecimento(false);
                      }}
                      noOptionsMessage={() => loadingEstabelecimentos
                        ? t("label.loading") + "..."
                        : t("information_message.no_records_found")}
                    />
                    {erroEstabelecimento && (
                      <FormHelperText>
                        <span className="obg">{t("label.required")}</span>
                      </FormHelperText>
                    )}
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <Button round color="primary" onClick={handleClickPesquisa} disabled={loading}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : t("label.search")}
                  </Button>
                </GridItem>
              </GridContainer>

              {registros.length > 0 && (
                <GridContainer style={{ marginBottom: "20px" }}>
                  <GridItem xs={12} sm={6} md={4}>
                    <FormControl fullWidth className={classes.formControl}>
                      <label className="select__label">{t("contract_audit.action")}</label>
                      <Select
                        classNamePrefix="select"
                        isClearable
                        styles={ValidSelect(false)}
                        placeholder={t("label.all")}
                        options={opcoesAcao}
                        value={filtroAcao}
                        onChange={setFiltroAcao}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={6} md={4}>
                    <FormControl fullWidth className={classes.formControl}>
                      <label className="select__label">{t("contract_audit.contract_type")}</label>
                      <Select
                        classNamePrefix="select"
                        isClearable
                        styles={ValidSelect(false)}
                        placeholder={t("label.all")}
                        options={opcoesContrato}
                        value={filtroContrato}
                        onChange={setFiltroContrato}
                      />
                    </FormControl>
                  </GridItem>
                </GridContainer>
              )}

              {notification.show && (
                <SnackbarContent
                  message={notification.message}
                  color={notification.color}
                  closeNotification={() => setNotification({ show: false })}
                  close
                />
              )}
            </CardBody>
          </Card>
        </GridItem>

        <GridItem xs={12}>
          <Card>
            <CardBody>
              {!buscaRealizada && !loading && (
                <p style={{ textAlign: "center", margin: "24px 0" }}>{t("contract_audit.initial_message")}</p>
              )}

              {loading && (
                <div style={{ textAlign: "center", margin: "24px 0" }}>
                  <CircularProgress />
                  <p>{t("label.loading")}...</p>
                </div>
              )}

              {buscaRealizada && !loading && dadosTabela.length === 0 && (
                <p style={{ textAlign: "center", margin: "24px 0" }}>{t("information_message.no_records_found")}</p>
              )}

              {!loading && dadosTabela.length > 0 && (
                <ReactTable
                  data={dadosTabela}
                  columns={columns}
                  defaultPageSize={10}
                  minRows={5}
                  showPaginationBottom={true}
                  sortable={false}
                  className="-striped -highlight"
                  previousText={t("button.previous")}
                  nextText={t("button.next")}
                  loadingText={t("label.loading") + "..."}
                  noDataText={t("information_message.no_records_found") + "!"}
                  pageText={t("label.page")}
                  ofText={t("label.of")}
                  rowsText={t("label.rows")}
                />
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
